import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { getMethodology, electMethod } from '../services/methodologyApi';
import type {
  MethodologyResponse, MethodologyResult, BiMethodSupportRow, ElectableApiMethod,
} from '../types/methodology';
import { MethodElectionCard } from '../components/attorney/MethodElectionCard';
import { CarrierCounterModelPanel } from '../components/attorney/CarrierCounterModelPanel';
import { HonestState } from '../components/workspace/primitives/HonestState';
import { FinancialValue } from '../components/shared/FinancialValue';

function mapMethodToApiMethod(methodId: string): ElectableApiMethod | null {
  const clean = methodId.replace(/^bi_/, '');
  if (clean === 'bottom_up' || clean === 'top_down' || clean === 'reconciled'
      || clean === 'projection_trend' || clean === 'two_period') {
    return clean;
  }
  return null;
}

export default function AttorneyMethodologyDefenseView(): React.ReactElement {
  const { caseId } = useParams<{ caseId: string }>();
  const [data, setData] = useState<MethodologyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [electingMethod, setElectingMethod] = useState<string | null>(null);
  const [electError, setElectError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    setError(null);
    try {
      const response = await getMethodology(caseId);
      setData(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load methodology data');
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Rows come from the server's support list in its FIXED catalog order — never re-sorted by amount,
  // never flagged recommended. Each row is paired with its methodology dossier by method_id.
  const supportRows = useMemo<BiMethodSupportRow[]>(
    () => (data?.bi_method_support ?? []).filter((r) => r.kind === 'bi_method'),
    [data?.bi_method_support],
  );
  const epiRow = useMemo(
    () => (data?.bi_method_support ?? []).find((r) => r.kind === 'coverage_line') ?? null,
    [data?.bi_method_support],
  );

  const filteredMethods = useMemo(() => {
    if (!data?.methods) return [];
    const methodMap = new Map<string, MethodologyResult>();
    data.methods.forEach((m) => methodMap.set(m.method_id, m));
    return supportRows
      .map((r) => methodMap.get(r.method_id))
      .filter((m): m is MethodologyResult => m != null);
  }, [data?.methods, supportRows]);

  const electedMethodId = data?.elected_method
    ? `bi_${data.elected_method}`
    : filteredMethods.find((m) => m.is_elected)?.method_id;

  const electedGross = useMemo(() => {
    const elected = filteredMethods.find((m) => m.method_id === electedMethodId);
    return elected?.gross ?? elected?.value ?? null;
  }, [filteredMethods, electedMethodId]);

  const supportFor = (methodId: string): BiMethodSupportRow | null =>
    supportRows.find((r) => r.method_id === methodId) ?? null;

  const handleElect = async (methodId: string) => {
    if (!caseId) return;
    const apiMethod = mapMethodToApiMethod(methodId);
    if (!apiMethod) return;

    setElectingMethod(methodId);
    setElectError(null);

    const previousData = data;

    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        elected_method: apiMethod,
        methods: prev.methods.map((m) => ({
          ...m,
          is_elected: m.method_id === methodId,
        })),
      };
    });

    try {
      await electMethod(caseId, apiMethod);
      await fetchData();
    } catch (err) {
      setData(previousData);
      const msg = err instanceof Error ? err.message : 'Election failed';
      const status = (err as { status?: number } | null)?.status;
      const detail = (err as { data?: { detail?: { reasons?: string[]; status?: string } } } | null)?.data?.detail;
      if (status === 409 && detail && typeof detail === 'object') {
        // The server's support gate said no — show ITS reasons, verbatim.
        setElectError(
          `Not electable (${(detail.status ?? 'not supportable').replace(/_/g, ' ')})` +
          (detail.reasons?.length ? ` — ${detail.reasons.join('; ')}` : '') +
          '. The record is unchanged.'
        );
      } else {
        setElectError(
          msg.includes('401') || msg.includes('403')
            ? 'Your role cannot elect methods.'
            : 'Election failed — the record is unchanged. Retry.'
        );
      }
    } finally {
      setElectingMethod(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 lg:p-8">
        <div className="mb-6">
          <div className="h-8 w-64 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded mb-2" />
          <div className="h-4 w-96 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded" />
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-96 bg-[var(--color-bg-surface)] border border-[var(--color-border)] rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="p-6 lg:p-8">
        <HonestState tier="no-input" scale="panel" label={error || 'Methodology data not available'} />
      </div>
    );
  }

  const hasStaleEngines = data.stale_engines && data.stale_engines.length > 0;

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="font-serif text-[28px] text-[var(--color-text-primary)] mb-2">
          Methodology &amp; Defense
        </h1>
        <p className="text-[13px] text-[var(--color-text-secondary)]">
          Select the elected methodology for this case. The elected figure feeds the sworn Proof of Loss, demand letter, and court bundle.
        </p>
      </div>

      {hasStaleEngines && (
        <div className="mb-6 p-4 border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
          <span className="text-[13px] font-semibold text-[var(--color-text-primary)]">
            Engine outputs stale:
          </span>{' '}
          <span className="text-[13px] text-[var(--color-text-secondary)]">
            {data.stale_engines.join(', ')} — figures below are from the last completed run; re-run before filing.
          </span>
        </div>
      )}

      <p className="mb-4 text-[12px] text-[var(--color-text-muted)]">
        Every computed method is shown with its support status, in a fixed order. Only a supportable
        method can be elected; the platform does not rank or recommend one.
      </p>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {filteredMethods.map((method) => (
          <MethodElectionCard
            key={method.method_id}
            method={method}
            support={supportFor(method.method_id)}
            isElected={method.method_id === electedMethodId}
            onElect={() => handleElect(method.method_id)}
            electing={electingMethod === method.method_id}
          />
        ))}
      </div>

      {epiRow && (
        <div className="mt-4 p-4 border border-[var(--color-border)] bg-[var(--color-bg-surface)] rounded-md" data-testid="epi-coverage-line">
          <div className="flex justify-between items-baseline">
            <span className="text-[11px] uppercase tracking-[0.08em] font-semibold text-[var(--color-text-muted)]">
              Coverage line — not a BI method
            </span>
            <FinancialValue value={epiRow.figure.gross} size="md" />
          </div>
          <div className="text-[13px] text-[var(--color-text-primary)] mt-1">{epiRow.label}</div>
          {epiRow.reasons.map((r, i) => (
            <p key={i} className="text-[12px] text-[var(--color-text-secondary)] mt-1">{r}</p>
          ))}
        </div>
      )}

      {electError && (
        <div className="mt-4 text-[13px] text-[var(--color-blocker)]">{electError}</div>
      )}

      <CarrierCounterModelPanel
        carrier={
          data.carrier ?? {
            available: false,
            attack_vector: null,
            attack_strength: null,
            daubert_score: null,
            alternative_figure: null,
            depo_questions: [],
          }
        }
        electedGross={electedGross}
      />

      <div className="mt-6 pt-4 border-t border-[var(--color-border)]">
        <p className="text-[11px] text-[var(--color-text-muted)]">
          Source: methodology engine v{data.methods[0]?.engine_version || 'unknown'} ·{' '}
          {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
        </p>
      </div>
    </div>
  );
}
